import { Bucket } from '@src/components/Bucket';
import { ORDER_SIDES } from '@src/tokens/order';
import { SideEnum } from '@talos/kyoko';
import { transparentize } from 'polished';
import styled, { css, keyframes } from 'styled-components';

const LEVEL_HEIGHT = 28;

export const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const totalBidAnimation = keyframes`
  0% {
    opacity: 0;
  }
  50% {
    opacity: 0;
    transform: translateX(5px);
  }
  100% {
    opacity: 1;
    transform: translateX(0);
  }
`;

const totalOfferAnimation = keyframes`
  0% {
    opacity: 0;
  }
  50% {
    opacity: 0;
    transform: translateX(-5px);
  }
  100% {
    opacity: 1;
    transform: translateX(0);
  }
`;

export const TotalPopup = styled.div`
  font-size: ${({ theme }) => theme.fontSizeSmall}rem;
  font-style: italic;
  height: ${LEVEL_HEIGHT}px;
  left: 0;
  line-height: 18px;
  padding: ${({ theme }) => theme.spacing4}px 0;
  pointer-events: none;
  position: absolute;
  right: 0;
  text-align: center;
  white-space: nowrap;
`;

export const Side = styled.span<{ side: string }>`
  color: ${({ side, theme }) => (side === SideEnum.Sell ? theme.colors.red.lighten : theme.colors.green.lighten)};
`;

export const Price = styled.span<{ isReadOnly?: boolean }>`
  cursor: ${({ isReadOnly }) => (isReadOnly ? 'normal' : 'pointer')};
  z-index: 1;
  margin: 0 ${({ theme }) => theme.spacing4}px;
  color: var(--colorTextImportant);
`;

export const LadderBg = styled.div<{ isBid?: boolean; isOffer?: boolean }>`
  position: absolute;
  height: ${LEVEL_HEIGHT}px;
  width: 100%;
  will-change: transform;
  transform-origin: center ${({ isBid }) => (isBid ? `right` : `left`)};
  transition:
    transform 100ms linear,
    opacity 100ms;
  background: ${({ theme }) => theme.colors.primary.dim};
  z-index: 0;

  ${props =>
    props.isBid &&
    `
    right: 0;
    transform-origin: 100% 50%;
  `}

  ${props =>
    props.isOffer &&
    `
    left: 0;
    transform-origin: 0% 50%;
  `}
`;

export const BidBucket = styled(Bucket)<any>`
  position: absolute;
  left: ${({ theme }) => theme.spacing16}px;
  z-index: 2;
`;

export const OfferBucket = styled(Bucket)<any>`
  position: absolute;
  right: ${({ theme }) => theme.spacing16}px;
  z-index: 2;
`;

type HeaderAlignmentProps = { $isLeftAligned?: boolean; $isRightAligned?: boolean };

const headerCell = css<HeaderAlignmentProps>`
  font-size: ${({ theme }) => theme.fontSizeTiny}rem;
  text-transform: uppercase;
  text-align: ${({ $isLeftAligned, $isRightAligned }) =>
    $isLeftAligned ? 'left' : $isRightAligned ? 'right' : 'center'};
`;

export const SpreadHeaderCell = styled.div<HeaderAlignmentProps>`
  ${headerCell};
  width: 96px;
  text-align: center;
`;
export const QtyHeaderCell = styled.div<HeaderAlignmentProps>`
  ${headerCell};
  width: 55px;

  ${({ theme, $isLeftAligned }) => $isLeftAligned && `padding-left: ${theme.spacing16}px;`}
  ${({ theme, $isRightAligned }) => $isRightAligned && `padding-right: ${theme.spacing16}px;`}
`;
export const PxHeaderCell = styled.div<HeaderAlignmentProps>`
  ${headerCell};
  width: 58px;
`;

export const OffersSideCell = styled.div<{ isReadOnly?: boolean; isActive: boolean }>`
  width: 50%;
  align-items: center;
  display: flex;
  position: relative;
  padding-right: 51px;
  justify-content: flex-end;
  height: ${LEVEL_HEIGHT}px;
  transition: 100ms;
  background: transparent;
  line-height: 1;
  user-select: none;
  z-index: 0;

  & ${TotalPopup} {
    animation: ${totalOfferAnimation} 200ms;
  }

  ${p =>
    !p.isReadOnly &&
    `
    &:hover {
      cursor: pointer;
    }
  `}

  ${p =>
    p.isActive &&
    `
      ${LadderBg} {
        opacity: 0;
      }
      ${OfferBucket} {
        border-color: transparent;
      }
      &, &:hover {
        background: ${p.theme.colors.green.dim};
      }
    `}
`;

export const Row = styled.div<{ activeSide?: string | boolean; showTotalEstimates?: string | boolean }>`
  position: relative;
  display: flex;
  transition: background 100ms ease;

  ${({ activeSide, showTotalEstimates, theme }) => `
    &:hover {
      ${showTotalEstimates && 'filter: brightness(130%);'}
      background: ${
        showTotalEstimates
          ? activeSide === ORDER_SIDES.SELL
            ? theme.colors.red.dim
            : activeSide === ORDER_SIDES.BUY
              ? theme.colors.green.dim
              : theme.colors.gray['020']
          : 'transparent'
      };

      ${LadderBg} {
        ${showTotalEstimates && 'opacity: 0'};
      }
    }

    ${BidsSideCell}:hover {
      ${!showTotalEstimates && 'filter: brightness(130%)'};
      background: ${
        !showTotalEstimates
          ? activeSide === ORDER_SIDES.SELL
            ? theme.colors.red.dim
            : activeSide === ORDER_SIDES.BUY
              ? theme.colors.green.dim
              : theme.colors.gray['020']
          : 'transparent'
      };

    }

    ${OffersSideCell}:hover {
      ${!showTotalEstimates && 'filter: brightness(130%)'};
      background: ${
        !showTotalEstimates
          ? activeSide === ORDER_SIDES.SELL
            ? theme.colors.red.dim
            : activeSide === ORDER_SIDES.BUY
              ? theme.colors.green.dim
              : theme.colors.gray['020']
          : 'transparent'
      };
    }
  `}

  &:nth-child(2) {
    ${LadderBg} {
      background: ${({ theme }) => transparentize(0.3, theme.colors.primary.default)};
    }
  }
  &:nth-child(3) {
    ${LadderBg} {
      background: ${({ theme }) => transparentize(0.4, theme.colors.primary.default)};
    }
  }
  &:nth-child(4) {
    ${LadderBg} {
      background: ${({ theme }) => transparentize(0.5, theme.colors.primary.default)};
    }
  }
  &:nth-child(5) {
    ${LadderBg} {
      background: ${({ theme }) => transparentize(0.6, theme.colors.primary.default)};
    }
  }
  &:nth-child(6) {
    ${LadderBg} {
      background: ${({ theme }) => transparentize(0.7, theme.colors.primary.default)};
    }
  }
`;

export const HeaderRow = styled.div`
  display: flex;
  margin-bottom: ${({ theme }) => theme.spacing4}px;
`;

export const BidsSideCell = styled.div<{ isActive?: boolean; isReadOnly?: boolean }>`
  width: 50%;
  align-items: center;
  display: flex;
  padding-left: 49px;
  position: relative;
  height: ${LEVEL_HEIGHT}px;
  transition: 100ms;
  background: transparent;
  user-select: none;
  z-index: 0;

  & ${TotalPopup} {
    animation: ${totalBidAnimation} 200ms;
  }

  ${p =>
    !p.isReadOnly &&
    `
    &:hover {
      cursor: pointer;
    }
  `}

  ${p =>
    p.isActive &&
    `
    ${LadderBg} {
      opacity: 0;
    }
    ${BidBucket} {
      border-color: transparent;
    }
    &, &:hover {
      background: ${p.theme.colors.red.dim};
    }
  `}
`;

const ownOrderAnim = keyframes`
  0% {
    transform: translateY(-55%) scale(0);
  }
  100% {
    transform: translateY(-55%) scale(1);
  }
`;

export const OwnOrderWrapper = styled.div<{ isBid?: boolean; isOffer?: boolean; hasMany?: boolean }>`
  position: absolute;
  top: 50%;
  transform: translateY(-55%);
  animation: ${ownOrderAnim} 200ms ease-out;

  ${p => p.isBid && `left: -2px;`}
  ${p => p.isOffer && `right: -2px;`}
`;

export const OwnOrderIndicator = styled.div<{ isBid?: boolean; isOffer?: boolean; isOpen?: boolean; hasMany: boolean }>`
  padding: 6px;

  &:before,
  &:after {
    transition: 50ms;
    display: block;
    height: 6px;
    width: 6px;
    transform-origin: center;
  }

  &:before {
    transform: translate(0px, 5px) rotate(45deg);
    background: ${({ theme }) => theme.colors.primary.lighten};
  }

  &:after {
    background: ${({ theme }) => theme.colors.primary.lighten};
    content: '';
  }

  ${p =>
    p.hasMany
      ? `
    &:before {
      content: '';
    }
    &:after {
      transform: translate(0px, -5px) rotate(45deg);
      box-shadow: 1px 1px rgba(0, 0, 0, 0.5);
    }
  `
      : `
    &:after {
      transform: rotate(45deg);
    }
  `}
`;

export const OwnOrderContent = styled.div`
  margin: ${({ theme }) => `-${theme.spacing4}px -${theme.spacing8}px`};
  color: ${({ theme }) => theme.colors.gray['070']};
  white-space: nowrap;
`;

export const OwnOrderRow = styled.div`
  padding: ${({ theme }) => theme.spacing8}px;
  width: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: center;

  & + & {
    border-top: ${({ theme }) => `1px solid ${theme.colors.gray['040']}`};
  }
`;

export const OwnOrderID = styled.div`
  margin-right: ${({ theme }) => theme.spacing8}px;
  color: ${({ theme }) => theme.colors.gray['090']};
  font-size: ${({ theme }) => theme.fontSizeSmall}rem;
  padding: 2px 6px;
  display: inline-block;
  border-radius: 999em;
  background: ${({ theme }) => theme.colors.gray['070']};
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

export const SpreadDisplay = styled.div<{ unit?: string | false }>`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  pointer-events: ${({ unit }) => (unit ? 'auto' : 'none')};
  z-index: 1;

  p {
    margin: 0;
    color: ${({ theme }) => theme.colors.gray['100']};
    position: relative;
    font-size: ${({ theme }) => theme.fontSizeTiny}rem;
    border-radius: ${({ theme }) => theme.borderRadiusDefault}px;
    background: ${({ theme }) => theme.colors.white.mute};
    padding: ${({ theme }) => `${theme.spacing4}px ${theme.spacing8}px`};
    line-height: 0.75rem;

    ${({ unit }) =>
      unit &&
      `&::after {
        white-space: nowrap;
        content: ' ${unit}';
      }
    `}
  }
`;
